/**
 * LA NATURE D'UN NOMBRE (§11 — « Toujours distinguer : donnée observée /
 * calcul / estimation / projection »).
 *
 * ⚠️ CE FICHIER VIT DANS `src/partage/` POUR LA RAISON HABITUELLE DE CE DÉPÔT :
 * le SERVICE produit ces valeurs, l'INTERFACE les dessine, et deux tables des
 * mêmes libellés divergeraient au premier correctif appliqué à une seule.
 * L'étage `interface` du Dockerfile ne copie que `frontend/` et `src/partage/`.
 * Rien d'autre que du calcul pur n'entre ici : ce fichier part dans le
 * navigateur.
 *
 * ⚠️ POURQUOI C'EST UN TYPE ET NON UNE ÉTIQUETTE D'ÉCRAN. Une mention
 * « estimation » posée à la main dans un composant se perd au premier
 * copier-coller, et le nombre continue de circuler sans elle. Ici, un nombre ne
 * peut pas exister sans sa nature : la structure l'impose au producteur.
 *
 * Le cas qui a motivé la rigueur : un prix au m² calculé sur la surface
 * D'AUJOURD'HUI pour une vente d'il y a deux ans. Le calcul aboutit, le nombre
 * est plausible, il entre dans la médiane du secteur — et il est faux. Seule la
 * traçabilité de ce sur quoi il repose permet de l'attraper.
 */

export type Nature =
  /** Lue au registre, telle quelle. Un prix déclaré dans un acte. */
  | 'observee'
  /** Dérivée d'observations par une arithmétique exacte. Un prix au m², une médiane. */
  | 'calcul'
  /** Inférée d'une population, avec incertitude. La valeur d'un bien sans transaction. */
  | 'estimation'
  /** Extrapolée AU-DELÀ de la période observée. Toujours la plus fragile. */
  | 'projection'
  /**
   * ⚠️ PAS UNE NATURE DE PLUS : LE REFUS DE CONCLURE. La population ne permet
   * rien d'affirmer. C'est ce qu'il faut rendre au lieu de zéro — « 0 vente
   * anormale » et « aucune vente à examiner » se ressemblent trait pour trait
   * sur un écran, et disent l'inverse l'un de l'autre.
   */
  | 'inconnu';

export interface Valeur<T = number> {
  /** `null` dès que la nature vaut `inconnu`. Jamais 0 pour dire « on ne sait pas ». */
  valeur: T | null;
  nature: Nature;
  /** Ce sur quoi elle repose, en clair. Destiné à être AFFICHÉ, pas journalisé. */
  base: string;
  /** Nombre d'observations qui la fondent. 0 impose `inconnu`. */
  observations: number;
}

export const LIBELLES_NATURE: Record<Nature, { court: string; long: string }> = {
  observee: { court: 'observé',
    long: 'Lu au registre tel qu\'il y est inscrit. Aucun calcul.' },
  calcul: { court: 'calculé',
    long: 'Dérivé d\'observations par une arithmétique exacte. Vérifiable pas à pas.' },
  estimation: { court: 'estimé',
    long: 'Inféré d\'un ensemble de transactions comparables. Porte une incertitude.' },
  projection: { court: 'projeté',
    long: 'Extrapolé au-delà de la période observée. Ce n\'est pas une prévision du marché : c\'est la tendance passée prolongée.' },
  inconnu: { court: 'indéterminé',
    long: 'Les observations disponibles ne permettent pas de conclure. Ce n\'est pas zéro.' },
};

/** Construit une valeur connue. La nature est explicite : jamais devinée. */
export function valeur<T>(v: T, nature: Exclude<Nature, 'inconnu'>, base: string, observations: number): Valeur<T> {
  return { valeur: v, nature, base, observations };
}

/**
 * Le refus de conclure.
 *
 * ⚠️ C'est la fonction la plus importante de ce fichier. Chaque fois qu'une
 * statistique de ce moteur porte sur un ensemble trop petit, elle passe par ici
 * — et l'écran affiche « indéterminé, 3 observations » au lieu d'une médiane
 * calculée sur trois ventes, qui aurait exactement l'allure d'une médiane
 * calculée sur trois mille.
 */
export function indetermine<T>(base: string, observations = 0): Valeur<T> {
  return { valeur: null, nature: 'inconnu', base, observations };
}
